import React, { useState, useEffect, useRef, useCallback, ReactChild } from 'react';
import { BigNumber } from 'ethers';
import { formatEther, parseEther } from 'ethers/lib/utils';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/macro';

import useMarkets from '../../contexts/MarketsContext';
import { Input1, Nav2, Label3, Small1 } from '../Text';
import { Autofill } from './autofill';

const MarketInputContainer = styled.div`
  display: flex;
  flex-direction column;
  width: 100%;
`;

const PositionsContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const MarketItem = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0.375rem 0.5rem;
  gap: 0.125rem;
  background: #222739;
  border-radius: 0.5rem;
`;

const InputWrapper = styled.div<{ error: boolean; disabled: boolean; baseInputEnabled: boolean }>`
  display: flex;
  flex: 1;
  max-width: calc(${({ baseInputEnabled }) => (baseInputEnabled ? '50%' : '100')} - 0.1875rem);
  box-sizing: border-box;
  flex-direction: row;
  background: ${({ disabled, theme }) => (disabled ? theme.bg3 : theme.bg0)};
  border-radius: 0.75rem;
  padding: 0.25rem;
  border: 0.066rem solid;
  border-color: ${({ error, theme }) => (error ? theme.red : 'rgba(0, 0, 0, 0)')};
`;

const NumberInput = styled(Input1)<{ disabled: boolean }>`
  background: none;
  border: none;
  color: ${({ disabled, theme }) => (disabled ? theme.bg5 : theme.white)};
  width: 100%;
  padding: 0 1.25rem;

  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &::-webkit-input-placeholder,
  &::placeholder {
    color: ${({ theme }) => theme.gray3};
  }
`;

const MarketOption = styled(Nav2)<{ disabled: boolean }>`
  display: flex;
  flex-direction: row;
  color: ${({ disabled, theme }) => (disabled ? theme.gray3 : theme.white)};
  padding: 0.44rem 0.7rem;
  margin: 0.125rem;
`;

const SelectedMarket = styled(MarketOption)`
  padding: 0;
`;

const ErrorText = styled(Small1)<{ disabled: boolean }>`
  margin: 0;
  margin-top: 0.375rem;
  color: ${({ disabled, theme }) => (disabled ? theme.bg5 : theme.red)};
`;

const TopText = styled(Label3)<{ disabled: boolean }>`
  margin: 0.375rem 0rem;
  color: ${({ theme, disabled }) => (disabled ? theme.bg5 : theme.gray4)};
  display: flex;
  flex-direction: row;
  gap: 0.375rem;

  b {
    color: white;
  }
`;

interface PositionInputProps {
  onChange: (value: BigNumber | undefined) => void;
  maxQuoteVal?: BigNumber;
  minQuoteVal?: BigNumber;
  disabled?: boolean;
  baseInputEnabled?: boolean;
  setQuoteInputElem?: (baseInputElem: HTMLInputElement) => void;
  placeholder?: string;
  className?: string;
  onBaseInputFocusChange?: (focused: boolean) => void;
  onQuoteInputFocusChange?: (focused: boolean) => void;
  autofillEnabled?: boolean;
  headerText?: ReactChild;
  customLabel?: string;
  customError?: string;
}

const fillAmounts = ['25%', '50%', '75%', 'MAX'];

export function PositionInput({
  onChange,
  disabled = false,
  baseInputEnabled = true,
  setQuoteInputElem,
  placeholder,
  className,
  onBaseInputFocusChange,
  onQuoteInputFocusChange,
  maxQuoteVal,
  minQuoteVal,
  headerText,
  autofillEnabled = false,
  customLabel,
  customError = '',
}: PositionInputProps) {
  const baseInputElem = useRef<HTMLInputElement>(null);
  const quoteInputElem = useRef<HTMLInputElement>(null);

  const { selectedMarket } = useMarkets();
  const { t } = useTranslation();

  const [autofillSelected, setAutofillSelected] = useState(-1);
  const [baseInput, setBaseInput] = useState<BigNumber>();
  const [quoteInput, setQuoteInput] = useState<BigNumber>();
  const [error, setError] = useState('');

  useEffect(() => {
    if (maxQuoteVal && quoteInput?.gt(maxQuoteVal)) {
      setError(t('Invalid leverage'));
    } else if (minQuoteVal && quoteInput?.lt(minQuoteVal)) {
      setError('Input is below the required minimum size.');
    } else {
      setError('');
    }

    if (baseInputElem.current && document.activeElement !== baseInputElem.current) {
      baseInputElem.current.value = baseInput
        ? Number(formatEther(baseInput)).toFixed(selectedMarket?.baseDecimals)
        : '';
    }

    if (quoteInputElem.current && document.activeElement !== quoteInputElem.current) {
      quoteInputElem.current.value = quoteInput ? Number(formatEther(quoteInput)).toFixed(2) : '';
    }
  }, [quoteInput, baseInput, maxQuoteVal, selectedMarket, minQuoteVal, customError, t]);

  useEffect(() => {
    if (!error) {
      onChange(quoteInput);
    }
  }, [quoteInput, onChange, error]);

  useEffect(() => {
    if (quoteInputElem.current && setQuoteInputElem) {
      setQuoteInputElem(quoteInputElem.current);
    }
  }, [quoteInputElem, setQuoteInputElem]);

  const handleBaseChange = (value: string) => {
    if (value === '') {
      setBaseInput(undefined);
      setQuoteInput(undefined);
    } else {
      let input = BigNumber.from(0);
      try {
        input = BigNumber.from(parseEther(value));
      } catch (err) {
        console.warn(err);
      }
      setBaseInput(input);
      setQuoteInput(selectedMarket ? input?.mulWei(selectedMarket?.price) : undefined);
    }
  };

  const handleBaseBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleBaseChange(e.target.value);
    setTimeout(() => {
      onBaseInputFocusChange?.(false);
    }, 100);
  };

  const handleBaseFocus = () => {
    onBaseInputFocusChange?.(true);
  };

  const handleBasePressEnter = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleBaseChange((e.target as HTMLInputElement).value);
      (e.target as HTMLInputElement).blur();
    }
  };

  const handleQuoteChange = (value: string) => {
    if (value === '') {
      setBaseInput(undefined);
      setQuoteInput(undefined);
    } else if (selectedMarket) {
      let input = BigNumber.from(0);
      try {
        input = BigNumber.from(parseEther(value));
      } catch (err) {
        console.warn(err);
      }
      setQuoteInput(input);
      setBaseInput(selectedMarket ? input?.divWei(selectedMarket.price) : undefined);
    }
  };

  const handleQuoteBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleQuoteChange(e.target.value);
    setTimeout(() => {
      onQuoteInputFocusChange?.(false);
    }, 100);
  };

  const handleQuoteFocus = () => {
    onQuoteInputFocusChange?.(true);
  };

  const handleQuotePressEnter = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleQuoteChange((e.target as HTMLInputElement).value);
      (e.target as HTMLInputElement).blur();
    }
  };

  const forceQuoteInput = useCallback(
    (value: string) => {
      if (quoteInputElem?.current) {
        quoteInputElem.current.focus();
        quoteInputElem.current.value = value;
        quoteInputElem.current.blur();
      }
    },
    [quoteInputElem],
  );

  const setAutofill = useCallback(
    (index: number) => {
      if (maxQuoteVal) {
        const portion = ((index + 1) / fillAmounts.length) * 100;
        setAutofillSelected(index);
        forceQuoteInput(formatEther(maxQuoteVal.mul(portion).div(100)));
      }
    },
    [maxQuoteVal, setAutofillSelected, forceQuoteInput],
  );

  return (
    <MarketInputContainer className={className}>
      {headerText && <TopText disabled={disabled}>{headerText}</TopText>}
      <PositionsContainer>
        {baseInputEnabled && (
          <InputWrapper
            error={!!(error || customError)}
            disabled={disabled}
            baseInputEnabled={baseInputEnabled}
          >
            <NumberInput
              placeholder={placeholder}
              type="number"
              min={0}
              onBlur={handleBaseBlur}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleBaseChange(e.target.value)
              }
              ref={baseInputElem}
              disabled={disabled}
              onKeyDown={handleBasePressEnter}
              onFocus={handleBaseFocus}
            />
            <SelectedMarket disabled={disabled}>
              <MarketItem>{selectedMarket?.base || t('N/A')}</MarketItem>
            </SelectedMarket>
          </InputWrapper>
        )}
        <InputWrapper
          error={!!(error || customError)}
          disabled={disabled}
          baseInputEnabled={baseInputEnabled}
        >
          <NumberInput
            placeholder={placeholder}
            type="number"
            min={0}
            onBlur={handleQuoteBlur}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleQuoteChange(e.target.value)}
            ref={quoteInputElem}
            disabled={disabled}
            onKeyDown={handleQuotePressEnter}
            onFocus={handleQuoteFocus}
          />
          <SelectedMarket disabled={disabled}>
            <MarketItem>{customLabel ?? (selectedMarket?.quote || t('N/A'))}</MarketItem>
          </SelectedMarket>
        </InputWrapper>
      </PositionsContainer>
      {(error || customError) && <ErrorText disabled={disabled}>{error || customError}</ErrorText>}
      {autofillEnabled && (
        <Autofill
          fillAmounts={fillAmounts}
          setAutofill={setAutofill}
          selected={autofillSelected}
          disabled={disabled}
        />
      )}
    </MarketInputContainer>
  );
}

PositionInput.defaultProps = {
  disabled: false,
  baseInputEnabled: true,
  className: '',
  placeholder: '0',
  setQuoteInputElem: (baseInputElem: HTMLInputElement) => baseInputElem,
  onBaseInputFocusChange: (focused: boolean) => focused,
  onQuoteInputFocusChange: (focused: boolean) => focused,
  autofillEnabled: false,
  headerText: '',
  customLabel: undefined,
  maxQuoteVal: undefined,
  minQuoteVal: undefined,
  customError: '',
};
